import { AxiosError, AxiosResponse, isAxiosError } from 'axios';

import {
  NotificationPreferencesApiFactory,
  NotificationPreferencesPayloadSchema as NotificationUserPreferences,
  NotificationPreferencesUnifiedUpstreamNotificationService as NotificationUserPreferencesData,
} from '@/clients/unifiedApi';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { LanguagePayload } from '@/models/translation';
import { getUnifiedApi } from '@/services/api/unifiedApi';
import { parseUser } from '@/services/parsers/user';
import session, { createSession } from '@/services/session';

import { GetUserApiResponse, GetUserByEmailResponse, UpdateUserApiResponse } from './models/user.models';

/**
 * Get authentication status by checking if the user is authenticated
 */
export const getAuthenticationStatus = async () => {
  const sessionWithSkipAuthRedirect = createSession(URLS.INTERNAL, { skipAuthRedirect: true });

  try {
    const response = await sessionWithSkipAuthRedirect.get<GetUserApiResponse>(`${URLS.USERS.ME}/`);
    return Boolean(response.data.email && response.data.activeCompanyId);
  } catch (error) {
    return false;
  }
};

/**
 * Get current user object
 */
export const getUser = async () => {
  const response = await session.get<GetUserApiResponse>(`${URLS.USERS.ME}/`);

  return parseUser(response.data);
};

/**
 * Get user by email
 * @returns
 */
export const getUserByEmail = async ({ email }: { email?: string }) => {
  const response = await session.get<PaginatedResponse<GetUserByEmailResponse>>(`${URLS.USERS.SEARCH}/?email=${email}`);

  return response.data;
};

export interface UpdateUserParams {
  data: {
    dateSignedTc?: string;
    dateModalDismissed?: string;
  };
}

/**
 * Update the current user object
 */
export const updateUser = async (params: UpdateUserParams) => {
  const { data } = params;
  const body = {
    ...data,
  };

  const response = await session.patch<UpdateUserApiResponse>(`${URLS.USERS.ME}/`, body);

  return parseUser(response.data);
};

/**
 * Update current language
 */
export const updateLanguage = (lang: LanguagePayload) => {
  return session.patch(`${URLS.USERS.SET_LANGUAGE}/`, lang).then((response) => response.data);
};

/**
 * Gets the split.io treatment
 * @param {string} features of split.io treatments
 */
export async function getSplits(features) {
  const customSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const response = await customSession.get(`${URLS.SPLIT}/?split_names=${features}`);

  return response.data;
}

/**
 * Retrieve available notification preferences
 */
export const getNotificationPreferences = async () => {
  const api = await getUnifiedApi(NotificationPreferencesApiFactory);

  const { data } = await api.notificationPreferencesList();

  return data;
};

/**
 * Retrieve user notification preferences
 */
export const getNotificationUserPreferences = async (userId: string) => {
  const api = await getUnifiedApi(NotificationPreferencesApiFactory);
  let preferences: NotificationUserPreferencesData[] | null = null;

  try {
    const { data = [] } = await api.notificationPreferencesUserUserIdGet({ userId });

    preferences = data;
  } catch (error) {
    if (!isAxiosError(error)) {
      throw error;
    }

    /**
     * NOTE:
     * If no records found on email-service API, it will return a 404 error. This also means user have all preferences selected by default.
     * So, instead just failing the request, it will set to null, and the component will now it will have to generate the preferences and update the API.
     */
    const { response } = error as AxiosError;
    const { status } = response as AxiosResponse;
    // otherwise, it will raise the returned error
    if (status !== 404) {
      throw error;
    }

    preferences = null;
  }

  return preferences;
};

/**
 * Update user notification preferences
 */
export const updateNotificationUserPreferences = async (userId: string, preferences: NotificationUserPreferences[]) => {
  const api = await getUnifiedApi(NotificationPreferencesApiFactory);

  const { data } = await api.notificationPreferencesUserUserIdPatch({
    userId,
    notificationPreferencesPayloadSchema: preferences,
  });

  return data;
};
